import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useT } from '../../lib/i18n';

// Horizontal-swipe slide carousel for the Busy Helper guide. CSS scroll-snap
// drives the gesture (no touch lib), an IntersectionObserver tracks the
// active slide, and arrow buttons + a row of dots let users navigate without
// swiping when they prefer.
//
// Each slide can have optional title / body / bullet list / pro tip / image.
// Image references are paths into /assets/help/ — if a file is missing the
// component renders a labeled placeholder so we can iterate on copy without
// blocking on screenshots.

export type HelperSlide = {
  /** i18n key for the slide title (mandatory). */
  titleKey: string;
  /** i18n key for the body paragraph (optional). */
  bodyKey?: string;
  /** i18n keys for bullet items (optional, rendered as a checked list). */
  bulletKeys?: string[];
  /** i18n key for a pro-tip callout (optional, gold tile). */
  tipKey?: string;
  /** Filename inside /assets/help/. If the image fails to load the tile
   *  shows a labeled placeholder instead of breaking. */
  image?: string;
  /** When set on the LAST slide, renders a "Take me there" CTA. */
  ctaRoute?: string;
};

export function HelperSlideshow({
  slides,
  onClose,
}: {
  slides: HelperSlide[];
  onClose: () => void;
}) {
  const t = useT();
  const navigate = useNavigate();
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [activeIdx, setActiveIdx] = useState(0);

  // Track the currently-snapped slide using IntersectionObserver. More
  // reliable than scrollLeft math when the container has padding/margin.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const slideEls = Array.from(track.querySelectorAll<HTMLElement>('[data-slide-idx]'));
    if (slideEls.length === 0) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) {
          const idx = Number((visible.target as HTMLElement).dataset.slideIdx ?? '0');
          setActiveIdx(idx);
        }
      },
      { root: track, threshold: [0.5, 0.75, 0.95] },
    );
    slideEls.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [slides.length]);

  function jumpTo(i: number) {
    const track = trackRef.current;
    if (!track) return;
    const target = track.querySelector<HTMLElement>(`[data-slide-idx="${i}"]`);
    target?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
  }

  return (
    <div className="space-y-3">
      {/* Track — flex row of full-width slides, snap on swipe */}
      <div
        ref={trackRef}
        className="flex overflow-x-auto snap-x snap-mandatory -mx-5 px-5 gap-4 pb-1"
        style={{ scrollbarWidth: 'none' }}
      >
        {slides.map((slide, i) => (
          <div
            key={i}
            data-slide-idx={i}
            className="snap-center shrink-0 w-full pr-4 last:pr-0"
            style={{ flex: '0 0 100%' }}
          >
            <SlideCard slide={slide} t={t} />
          </div>
        ))}
      </div>

      {/* Pagination dots */}
      {slides.length > 1 && (
        <div className="flex items-center justify-center gap-1.5">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => jumpTo(i)}
              aria-label={t('helper.slide.jumpTo', { n: i + 1 })}
              className={`w-2 h-2 rounded-full transition-colors ${
                i === activeIdx ? 'bg-navy-900 dark:bg-gold-500' : 'bg-line hover:bg-ink-500/50'
              }`}
            />
          ))}
        </div>
      )}

      {/* Footer — prev/next + final-slide CTA */}
      <div className="flex items-center justify-between gap-2 pt-1">
        <button
          type="button"
          onClick={() => jumpTo(Math.max(0, activeIdx - 1))}
          disabled={activeIdx === 0}
          className="h-10 px-3 rounded-lg border border-line bg-surface text-sm font-semibold text-ink-700 disabled:opacity-40 inline-flex items-center gap-1.5"
        >
          <ChevronLeftIcon className="w-3.5 h-3.5" />
          {t('helper.slide.prev')}
        </button>
        <span className="text-[11px] text-ink-500 tabular-nums">
          {activeIdx + 1} / {slides.length}
        </span>
        {activeIdx < slides.length - 1 ? (
          <button
            type="button"
            onClick={() => jumpTo(Math.min(slides.length - 1, activeIdx + 1))}
            className="h-10 px-3 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 text-sm font-semibold inline-flex items-center gap-1.5"
          >
            {t('helper.slide.next')}
            <ChevronRightIcon className="w-3.5 h-3.5" />
          </button>
        ) : slides[activeIdx]?.ctaRoute ? (
          <button
            type="button"
            onClick={() => {
              navigate(slides[activeIdx]!.ctaRoute!);
              onClose();
            }}
            className="h-10 px-3 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 text-sm font-bold inline-flex items-center gap-1.5"
          >
            {t('helper.guide.takeMe')}
            <ArrowRightIcon className="w-3.5 h-3.5" />
          </button>
        ) : (
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-3 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 text-sm font-bold"
          >
            {t('helper.slide.done')}
          </button>
        )}
      </div>
    </div>
  );
}

function SlideCard({
  slide,
  t,
}: {
  slide: HelperSlide;
  t: (key: string, vars?: Record<string, string | number>) => string;
}) {
  return (
    <div className="space-y-3">
      {slide.image && <SlideImage filename={slide.image} alt={t(slide.titleKey)} />}
      <h3 className="font-display text-xl leading-tight">{t(slide.titleKey)}</h3>
      {slide.bodyKey && (
        <p className="text-[14px] text-ink-700 leading-relaxed">{t(slide.bodyKey)}</p>
      )}
      {slide.bulletKeys && slide.bulletKeys.length > 0 && (
        <ul className="space-y-1.5">
          {slide.bulletKeys.map((k) => (
            <li key={k} className="flex items-start gap-2 text-[13px] text-ink-700 leading-relaxed">
              <CheckIcon className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
              <span>{t(k)}</span>
            </li>
          ))}
        </ul>
      )}
      {slide.tipKey && (
        <div className="rounded-xl border border-gold-500/40 bg-gold-500/8 p-3">
          <div className="flex items-start gap-2">
            <LightbulbIcon className="w-4 h-4 text-gold-500 shrink-0 mt-0.5" />
            <p className="text-[13px] text-ink-700 leading-relaxed">{t(slide.tipKey)}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function SlideImage({ filename, alt }: { filename: string; alt: string }) {
  // If the asset doesn't load, swap to a placeholder caption rather than
  // showing a broken image icon. Lets us ship slide content before captures
  // are dropped in.
  const [errored, setErrored] = useState(false);
  if (errored) {
    return (
      <div className="aspect-[16/10] rounded-xl border border-dashed border-line bg-bg-alt grid place-items-center text-center px-4">
        <div>
          <CameraIcon className="w-6 h-6 mx-auto text-ink-500 mb-1.5" />
          <p className="text-[11px] text-ink-500 leading-tight">
            <code className="px-1 py-0.5 rounded bg-surface text-[10px]">/assets/help/{filename}</code>
          </p>
          <p className="text-[10px] text-ink-500/70 mt-1">Drop a screenshot at this path.</p>
        </div>
      </div>
    );
  }
  return (
    <img
      src={`/assets/help/${filename}`}
      alt={alt}
      onError={() => setErrored(true)}
      className="aspect-[16/10] rounded-xl border border-line w-full object-cover bg-bg-alt"
    />
  );
}

/* ============================ icons ============================ */

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}
function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
function ArrowRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}
function LightbulbIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9 18h6M10 22h4" />
      <path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.1V18h6v-1.2c0-.8.4-1.6 1-2.1A7 7 0 0 0 12 2z" />
    </svg>
  );
}
function CameraIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}
